import { useMemo } from "react";

import {
  PortfolioAdditionalStatTile,
  PortfolioCardShell,
  PortfolioCardsList,
  PortfolioMetricCard,
  PortfolioTrialHeader,
} from "@/views/Portfolio/components";
import type {
  PortfolioAdditionalStat,
  PortfolioMetricPlanned,
  PortfolioMetricView,
  PortfolioPlanFrequency,
  PortfolioTrialHeaderInfo,
  TrialInfo,
} from "@/views/Portfolio/types";
import {
  buildPerSiteAdditionalStat,
  buildPortfolioAdditionalStat,
  derivePortfolioMetricView,
  indexTrialsById,
  resolvePortfolioTrialHeader,
} from "@/views/Portfolio/utils";
import {
  formatNumberWithGrouping,
  formatStatValue,
} from "@/utils/numberFormatting";

type PlannedFixture = {
  value?: number | null;
  date?: string | null;
  frequency?: PortfolioPlanFrequency | null;
} | null;

type EnrollmentRow = {
  trial_id: string;
  percent_to_plan?: number | null;
  percent_to_plan_delta_30d?: number | null;
  enrolled?: number | null;
  target?: number | null;
  enrolled_planned?: PlannedFixture;
  enrolled_delta_30d?: number | null;
  enrolled_total_count?: number | null;
  enrollment_rate?: number | null;
  enrollment_rate_delta_30d?: number | null;
  enrollment_rate_note?: string | null;
  total_screened?: number | null;
  total_screened_delta_30d?: number | null;
  // Legacy bare screened-planned count, superseded by screened_planned but
  // retained in the fixture per story requirements; not read when building views.
  screened_planned_count?: number | null;
  screened_planned?: PlannedFixture;
  screened_total_count?: number | null;
  in_screening_count?: number | null;
  in_screening_count_delta_30d?: number | null;
  screen_failure_percent?: number | null;
  screen_failure_percent_delta_30d?: number | null;
  per_site_enrolled_avg?: number | null;
  per_site_enrolled_sd?: number | null;
  per_site_enrolled_range_low?: number | null;
  per_site_enrolled_range_high?: number | null;
  per_site_rate_avg?: number | null;
  per_site_rate_sd?: number | null;
  per_site_rate_range?: string | null;
  per_site_screened_avg?: number | null;
  per_site_screened_sd?: number | null;
  per_site_screened_range_low?: number | null;
  per_site_screened_range_high?: number | null;
  per_site_failure_avg?: number | null;
  per_site_failure_sd?: number | null;
  per_site_failure_range_low?: number | null;
  per_site_failure_range_high?: number | null;
  expanded?: boolean;
};

type PortfolioEnrollmentCard = PortfolioTrialHeaderInfo & {
  screenedView: PortfolioMetricView;
  inScreeningView: PortfolioMetricView;
  screenFailureView: PortfolioMetricView;
  enrolledView: PortfolioMetricView;
  additionalStats: PortfolioAdditionalStat[];
  initiallyExpanded: boolean;
};

const ENROLLMENT_ADDITIONAL_STAT_ORDER = [
  "screened",
  "in-screening",
  "screen-failure",
  "enrolled",
] as const;

const ENROLLMENT_ADDITIONAL_STAT_UNITS: Record<
  (typeof ENROLLMENT_ADDITIONAL_STAT_ORDER)[number],
  string | undefined
> = {
  screened: undefined,
  "in-screening": undefined,
  "screen-failure": "%",
  enrolled: undefined,
};

const parsePlannedDate = (value?: string | null): Date | null => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (!match) return null;

  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(year, month - 1, day);

  return parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
    ? parsed
    : null;
};

const buildPlanned = (raw: PlannedFixture): PortfolioMetricPlanned => ({
  value: raw?.value ?? null,
  date: parsePlannedDate(raw?.date),
  frequency: raw?.frequency ?? null,
});

function EnrollmentCardView({ card }: { card: PortfolioEnrollmentCard }) {
  return (
    <PortfolioCardShell
      header={
        <PortfolioTrialHeader
          trial={card}
          titleClassName="!font-bold"
          linkToTrial
        />
      }
      metricColumns={4}
      additionalColumns={4}
      initiallyExpanded={card.initiallyExpanded}
      additionalContent={ENROLLMENT_ADDITIONAL_STAT_ORDER.map(
        (statKey, index) => (
          <PortfolioAdditionalStatTile
            key={`${card.trial_id}-${statKey}`}
            stat={card.additionalStats[index] ?? null}
            unit={ENROLLMENT_ADDITIONAL_STAT_UNITS[statKey]}
          />
        ),
      )}
    >
      <PortfolioMetricCard label="SCREENED" view={card.screenedView} />
      <PortfolioMetricCard label="IN SCREENING" view={card.inScreeningView} />
      <PortfolioMetricCard
        label="SCREEN FAILURE %"
        view={card.screenFailureView}
        suffix="%"
        progressTone="magenta"
        invertDelta
      />
      <PortfolioMetricCard label="ENROLLED" view={card.enrolledView} />
    </PortfolioCardShell>
  );
}

function buildEnrollmentAdditionalStats(
  row: EnrollmentRow,
): PortfolioAdditionalStat[] {
  return [
    buildPerSiteAdditionalStat(
      row.per_site_screened_avg,
      row.per_site_screened_sd,
      row.per_site_screened_range_low,
      row.per_site_screened_range_high,
      1,
    ),
    null,
    buildPortfolioAdditionalStat(
      formatStatValue(row.per_site_failure_avg, row.per_site_failure_sd, 0)
        ? `${formatNumberWithGrouping(row.per_site_failure_avg ?? 0)} ± ${formatNumberWithGrouping(row.per_site_failure_sd ?? 0)}`
        : null,
      row.per_site_failure_range_low,
      row.per_site_failure_range_high,
    ),
    buildPerSiteAdditionalStat(
      row.per_site_enrolled_avg,
      row.per_site_enrolled_sd,
      row.per_site_enrolled_range_low,
      row.per_site_enrolled_range_high,
      1,
    ),
  ];
}

function buildEnrollmentCard(
  row: EnrollmentRow,
  trialsById: Map<string, TrialInfo>,
): PortfolioEnrollmentCard {
  const header = resolvePortfolioTrialHeader(
    row.trial_id,
    trialsById.get(row.trial_id),
  );
  const lifecycle = header.isComplete ? "final" : "active";
  const deltaEnabled = !header.isComplete;

  const screenedActual = row.total_screened ?? null;
  const screenedView = derivePortfolioMetricView({
    actual: screenedActual,
    planned: buildPlanned(row.screened_planned),
    total: row.screened_total_count ?? null,
    delta30d: deltaEnabled ? (row.total_screened_delta_30d ?? null) : null,
    lifecycle,
    planTracked: true,
  });

  const inScreeningView = derivePortfolioMetricView({
    actual: row.in_screening_count ?? null,
    planned: buildPlanned(null),
    total: null,
    delta30d: deltaEnabled ? (row.in_screening_count_delta_30d ?? null) : null,
    lifecycle,
    planTracked: false,
  });

  const screenFailureView = derivePortfolioMetricView({
    actual: row.screen_failure_percent ?? null,
    planned: buildPlanned(null),
    total: null,
    delta30d: deltaEnabled
      ? (row.screen_failure_percent_delta_30d ?? null)
      : null,
    lifecycle,
    planTracked: false,
  });

  const enrolledActual = row.enrolled ?? null;
  const enrolledView = derivePortfolioMetricView({
    actual: enrolledActual,
    planned: buildPlanned(row.enrolled_planned),
    total: row.enrolled_total_count ?? null,
    delta30d: deltaEnabled ? (row.enrolled_delta_30d ?? null) : null,
    lifecycle,
    planTracked: true,
  });

  return {
    ...header,
    screenedView,
    inScreeningView,
    screenFailureView,
    enrolledView,
    additionalStats: buildEnrollmentAdditionalStats(row),
    initiallyExpanded: row.expanded ?? false,
  };
}

const buildCards = (
  rows: EnrollmentRow[],
  trialsById: Map<string, TrialInfo>,
) => rows.map((row) => buildEnrollmentCard(row, trialsById));

export function PortfolioEnrollmentTrendsTab({
  data,
  trials,
}: {
  data?: EnrollmentRow[];
  trials?: TrialInfo[];
}) {
  const cards = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const trialSource = trials ?? [];
    const trialsById = indexTrialsById(trialSource);

    return buildCards(rows, trialsById);
  }, [data, trials]);

  return (
    <PortfolioCardsList>
      {cards.map((card) => (
        <EnrollmentCardView key={card.trial_id} card={card} />
      ))}
    </PortfolioCardsList>
  );
}
